import { useRef, useState } from "react";
import { cx } from "./ui";

/**
 * Six cells over one input, for the authenticator code a destructive request
 * carries — a delete, or a suspension of several tenants at once.
 *
 * The input is what receives focus, keystrokes, paste and the label; the cells
 * only draw it. Keeping it a single control is what makes autofill from an
 * authenticator app work — `one-time-code` fills one field, not six.
 */
export function OtpCells({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const digits = value.split("");
  const active = Math.min(value.length, 5);

  return (
    <div className="relative">
      <div
        className="grid grid-cols-6 gap-2"
        onClick={() => ref.current?.focus()}
        aria-hidden="true"
      >
        {Array.from({ length: 6 }, (_, i) => {
          const filled = i < digits.length;
          const cursor = focused && i === active && value.length < 6;
          return (
            <div
              key={i}
              className={cx(
                "flex h-12 items-center justify-center rounded-md border font-mono text-xl text-fg transition-colors duration-150",
                filled ? "border-line-strong bg-raised" : "border-line bg-canvas",
                cursor && "border-accent ring-2 ring-accent/20",
              )}
            >
              {digits[i] ?? (cursor ? <span className="otp-caret h-6 w-px bg-accent" /> : null)}
            </div>
          );
        })}
      </div>
      <input
        ref={ref}
        id={id}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, "").slice(0, 6))}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        maxLength={6}
        aria-label="Authenticator code"
        className="absolute inset-0 h-full w-full cursor-text opacity-0"
      />
    </div>
  );
}
